import React, { useState } from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';

import CustomRatingsRadio, { 
  customRatingsSourceId,
  hasCustomRatingsId,
  SOURCE_CUSTOM_SEVENTEENLANDS
} from '../components/widgets/CustomRatings';
import Expan      from '../components/widgets/Expan';
import LabelRadio from '../components/widgets/LabelRadio';
import Switch     from '../components/widgets/LabelSwitch';

import SeventeenLands from '../services/seventeen-lands';
import GoogleSheets from '../services/google-sheets';
import { SET_RATINGS as CHORD_SET_RATINGS } from '../services/constants/chord-o-calls';
import { SET_RATINGS as DARKEST_MAGE_SET_RATINGS } from '../services/constants/darkest-mage';
import { SET_RATINGS as LOL_SET_RATINGS} from '../services/constants/lords-of-limited';

import { lookupByLowerCaseCardName } from '../helpers/card.helpers';
import { 
  RATING_SCORES_PROPERTY, 
  RATING_SET_PROPERTY, 
  RATING_TIER_PROPERTY } from '../constants';

const SOURCE_DARKEST_MAGE = "darkestmage";
const SOURCE_LORDS_OF_LIMITED = "lordsoflimited";
const SOURCE_CHORD_O_CALLS = "chord_o_calls";
const SOURCE_CUSTOM = "custom";

const LOCAL_STORAGE_RATINGS_SELECTED_SOURCE = 'ratings-selected-source';

export default function TierListRatings({ sets, cards, disabled, initializing, updateCardMetadataFn, onAddCustomRatings }) {
  const [isRatingsSwitchOn, setRatingsSwitch] = useState(false);
  const [selectedRatingsSource, setSelectedRatingsSource] = useState();
  const [isLoadingRatings, setLoadingRatings] = useState(false);
  // output messages
  const [warning, setWarning] = useState();
  const [error, setError] = useState();

  // Use a reference for state/props that need access to the freshest values during async processing
  // https://stackoverflow.com/a/61957390
  const ratingsSwitchRef = React.useRef(isRatingsSwitchOn);
  ratingsSwitchRef.current = isRatingsSwitchOn;
  const selectedRatingsSourceRef = React.useRef(selectedRatingsSource);
  selectedRatingsSourceRef.current = selectedRatingsSource;
  const setsRef = React.useRef(sets);
  setsRef.current = sets;
  const cardsRef = React.useRef(cards);
  cardsRef.current = cards;

  /*
   * Should this tier list ratings component be disabled:
   *  - Component is `disabled` or `initializing` (in props)
   *  - No available ratings sources for the current sets
   */
  const isDisabled = disabled || initializing || !availableRatingsSources(sets)?.length;
 
  /**
   * Helper function to determine if the given ratings source is currently the active selection
   * by using the current value of the reference for each state value.
   */
  const isCurrentlyActive = (source) =>
    ratingsSwitchRef.current && (source === selectedRatingsSourceRef.current);
  
  /**
   * Reset the default state of the component
   */
  const resetState = (source) => {
    setRatingsSwitch(false);
    setSelectedRatingsSource(source);
    setLoadingRatings(false);
    // clear warning and error messages
    setWarning(/* undefined */);
    setError(/* undefined */);
  }

  /**
   * Refresh the default selected ratings source after `initializing` and when the sets change.
   */
  useDeepCompareEffect(() => {
    const availableSources = availableRatingsSources(sets);
    if (availableSources.length) {
      // reset to previously selected source, otherwise the first available source
      const previousSource = localStorage.getItem(LOCAL_STORAGE_RATINGS_SELECTED_SOURCE);
      const source = availableSources.includes(previousSource) ? previousSource : availableSources[0];
      // set state values and perform any corresponding state updates
      resetState(source);
    }
    else if (!initializing) {
      // show message if no longer `initializing` and still no card rating sources (unless no cards)
      if (cardsRef.current.length > 0) {
        setWarning('No card ratings' + 
          // only add "for: ..." if sets exist
          ((sets.length > 0) ? ` for: ${sets.map(set => set.toUpperCase()).join(', ')}` : ''));
      }
    }
    else /* if (initializing) */ {
      // reset/clear the ratings source `initializing` has changed to true
      resetState(/* undefined */);
    }
  }, [initializing, sets]);

  /**
   * Helper function to visually indicate that card GIH WR is loading, 
   * both in this widget and on the individual cards
   */
  const showLoadingRatings = () => {
    // show 'Loading...' message on this component
    setLoadingRatings(true);
    // set each individual card rating to `null` to indicate it is loading but has no value
    // See `Card.maybeRenderRating`
    updateCardMetadataFn(
      // [ { cardId: 0, RATING_TIER_PROPERTY: null, RATING_SCORES_PROPERTY: null } ]
      cardsRef.current.map(card => (
        {
          cardId: card.cardId,
          [RATING_TIER_PROPERTY]: null,
          [RATING_SCORES_PROPERTY]: null
        }
      ))
    );
  }

  /**
   * Helper function to clear the visual indicators that card ratings are loading
   * both in this widget and on the individual cards that do NOT have `cardRatingData`
   * 
   * @param {*} cardRatingData 
   * @returns 
   */
  const hideLoadingRatings = (cardRatingData) => {
    setLoadingRatings(false);
    // find those cardIds that were NOT in the results; clear their GIHWR status
    const cardIdsWithRatings = new Set(cardRatingData.map(data => data.cardId));
    const cardIdsToClear = cardsRef.current.flatMap(card => {
      if (cardIdsWithRatings.has(card.cardId)) return [];
      else return [card.cardId];
    });

    return clearCardRatings(cardIdsToClear);
  }

  /**
   * Helper function to clear the card ratings
   * 
   * @param {*} cardIdsToClear 
   * @returns 
   */
  const clearCardRatings = (cardIdsToClear = cardsRef.current.map(card => card.cardId)) => {
    // set all ratings to `undefined` to overwrite the attributes:
    // [ { cardId: 0, ratings: undefined, tier: undefined }, ... ]
    const idsWithoutRatings = cardIdsToClear.map(cardId => 
      ({
        cardId, 
        [RATING_TIER_PROPERTY]: undefined,
        [RATING_SCORES_PROPERTY]: undefined,
        [RATING_SET_PROPERTY]: undefined
      }));
    return updateCardMetadataFn(idsWithoutRatings);
  }
  
  /**
   * Helper function to apply the retrieved card ratings, if still active
   */
  const maybeApplyRatings = (results, loadedSource) => {
    return Promise.resolve(mapCardIdsToRatings(cardsRef.current, results.ratings))
      .then((cardRatingData) => {
        // before updating, verify that the switch is not off and tier list ratings should 
        // still be shown AND that the `selectedRatingsSource` is the same as the loaded data
        if (isCurrentlyActive(loadedSource)) {
          if (cardRatingData.length) {
            updateCardMetadataFn(cardRatingData);
          }
          // show a warning iff results were found (results.ratings), but no card matches (cardRatingData)
          else if (Object.keys(results.ratings).length && cardsRef.current.length > 0) {
            setWarning('No card ratings matched any cards in this pool')
          }
        }
        else {
          console.info("Skipping tier list rating data from now-inactive request");
        }
        // return the card rating data (for access to the cardIds being updated)
        // NOTE that this is NOT chained off of the actual call to `updateCardMetadataFn`
        return cardRatingData;
      });
  }

  /**
   * Helper function to show error/warning messages, if still active
   * 
   * @param {*} results 
   * @param {*} loadedSource 
   */
  const maybeShowMessages = (results, loadedSource) => {
    // exit early and show no messages if the switch is toggled *off*
    // or if the selected format is different from the loaded data
    if (!isCurrentlyActive(loadedSource)) {
      console.info("Skipping error messages from now-inactive request:", loadedSource);
      return; // exit
    }

    if (results.missing.length) {
      setWarning('No card ratings found for: ' + 
        results.missing.map(set => set.toUpperCase()).join(', '));
    }
    // No card ratings - set an error message and be done
    else if (!Object.keys(results.ratings).length) {
      setError('Failed to load card ratings');
      return; // exit
    }

    // Partial card ratings - set error and/or warning messages
    if (results.errors.length) {
      setError('Failed to load card ratings: ' + 
        results.errors.map(set => set.toUpperCase()).join(', '));
    }
  }

  /**
   * Clear and refresh the card ratings for the given ratings source
   * 
   * @param {*} isRatingsSwitchOn 
   * @param {*} ratingsSource 
   */
  const refreshCardRatings = (isRatingsSwitchOn, ratingsSource) => {
    // clear warning and error messages
    setWarning(/* undefined */);
    setError(/* undefined */);
    // always clear any previous card ratings when triggered
    clearCardRatings();
    // then, if the switch is on, load the card ratings
    if (isRatingsSwitchOn) {
      // visually indicate that card ratings are loading
      showLoadingRatings();
      // only fetch card ratings for those card pool sets that have ratings from the selected source
      const availableSets = availableSetsForSource(setsRef.current, ratingsSource);
      const cardRatingsAndErrors = fetchCardRatings(ratingsSource, availableSets);
      // handle results (card ratings and errors)
      const showRatings = cardRatingsAndErrors.then(results => maybeApplyRatings(results, ratingsSource));
      const handleErrors = cardRatingsAndErrors.then(results => maybeShowMessages(results, ratingsSource));      
      // loading is done if this load is still active (i.e., no changes in selected format)
      Promise.all([showRatings, handleErrors]).then(([cardRatingData, _]) => {
        if(isCurrentlyActive(ratingsSource)) {
          // hide visual indicators that card ratings are loading
          console.debug("No longer loading from", ratingsSource);
          hideLoadingRatings(cardRatingData);
        }
      });
    }
    // otherwise just set loading status to false
    else setLoadingRatings(false);
  }

  const toggleRatingsSwitch = (selected) => {
    setRatingsSwitch(selected);
    refreshCardRatings(selected, selectedRatingsSourceRef.current)
  }

  const changeRatingsSource = (ratingsSource) => {
    setSelectedRatingsSource(ratingsSource);
    // store as the preferred ratings source
    if (ratingsSource) {
      localStorage.setItem(LOCAL_STORAGE_RATINGS_SELECTED_SOURCE, ratingsSource);
    }
    refreshCardRatings(ratingsSwitchRef.current, ratingsSource);
  }

  /**
   * Helper method to render error/warning messages when the specified source is selected/active
   * 
   * @param {*} ratingsSource the rating source being rendered
   */
  const renderMessages = (ratingsSource) => {
    const noSourceSelected = !(selectedRatingsSource || ratingsSource);
    if (noSourceSelected || isCurrentlyActive(ratingsSource)) {
      return <>
        {isLoadingRatings && <div className="message pulse"><span>Loading...</span></div>}
        {error && <div className="message error"><span>{error}</span></div>}
        {warning && <div className="message warning"><span>{warning}</span></div>}
      </>;
    }
  }

  // render the component
  return <div className="ratings">
    <Switch 
        className="ratings-show-switch"
        disabled={isDisabled} 
        onChange={toggleRatingsSwitch} 
        checked={isRatingsSwitchOn}>
      Show card ratings
    </Switch>
    {renderMessages()}
    {maybeRenderRatingsSources(sets, selectedRatingsSource, 
      /* disabled = */ (isDisabled || !isRatingsSwitchOn),
      /* renderMessagesFn = */ (ratingsSource) => renderMessages(ratingsSource),
      /* setRatingsSourceFn = */ (ratingsSource) => changeRatingsSource(ratingsSource)
    )}
    <div className="ratings-custom">
      <CustomRatingsRadio 
        disabled={isDisabled || !isRatingsSwitchOn}
        initializing={initializing}
        value={SOURCE_CUSTOM}
        checked={selectedRatingsSource === SOURCE_CUSTOM}
        onChange={() => {
          setRatingsSwitch(true);
          setSelectedRatingsSource(SOURCE_CUSTOM);
          // store 'custom' as the preferred ratings source
          localStorage.setItem(LOCAL_STORAGE_RATINGS_SELECTED_SOURCE, SOURCE_CUSTOM);
          refreshCardRatings(true, SOURCE_CUSTOM)
        }} 
        onDelete={() => {
          if (SOURCE_CUSTOM === selectedRatingsSourceRef.current) {
            const availableSources = availableRatingsSources(setsRef.current);
            resetState(availableSources[0]);
            clearCardRatings();
          }
        }}
        onAddCustomRatings={onAddCustomRatings} />
      {renderMessages(SOURCE_CUSTOM)}
    </div>
  </div>;
}

function maybeRenderRatingsSources(sets, selectedSource, disabled, renderMessagesFn, setRatingsSourceFn) {
  return <>
    {maybeRenderDarkestMage(sets, selectedSource, disabled, renderMessagesFn, setRatingsSourceFn)}
    {maybeRenderChordOCalls(sets, selectedSource, disabled, renderMessagesFn, setRatingsSourceFn)}
    {maybeRenderLordsOfLimited(sets, selectedSource, disabled, renderMessagesFn, setRatingsSourceFn)}
  </>;
}

function maybeRenderDarkestMage(sets, selectedSource, disabled, renderMessagesFn, setRatingsSourceFn) {
  const setsLinks = sets.filter(set => DARKEST_MAGE_SET_RATINGS.hasOwnProperty(set))
    .map((set, idx) => {
      const setUrl = GoogleSheets.spreadsheetUrl(DARKEST_MAGE_SET_RATINGS[set].ratings_id);
      return <React.Fragment key={idx}>
        {/* eslint-disable-line */}<a className="ratings-set-code" 
            href={setUrl} target="_blank" rel="noopener">
          {set.toUpperCase()}
        </a>
      </React.Fragment>;
    });

  if (setsLinks.length) {
    // wrap in an Expan if more than one source link
    const setsSpan = (setsLinks.length === 1) ?
      <span>{setsLinks}</span>
      : <Expan className="ratings-sources" title="Show sources by set" initial="[&hellip;]">{setsLinks}</Expan>;

    return <>
      <div className="ratings-darkestmage">
        <LabelRadio
            name="ratings"
            id="sealed-darkestmage"
            disabled={disabled}
            value={SOURCE_DARKEST_MAGE}
            checked={selectedSource === SOURCE_DARKEST_MAGE}
            onChange={() => setRatingsSourceFn(SOURCE_DARKEST_MAGE)} >
          <span>
            <em>Sealed</em> Ratings from 
            <br/>
            {/* eslint-disable-line */} <a href="https://twitch.tv/Darkest_Mage/videos"
              target="_blank" 
              rel="noopener">Darkest Mage</a>:
          </span> {setsSpan}
        </LabelRadio>
        {renderMessagesFn(SOURCE_DARKEST_MAGE)}
      </div>
    </>;
  }
  else return <></>;
}

function maybeRenderChordOCalls(sets, selectedSource, disabled, renderMessagesFn, setRatingsSourceFn) {
  const setsLinks = sets.filter(set => CHORD_SET_RATINGS.hasOwnProperty(set))
    .map((set, idx) => {
      const setUrl = SeventeenLands.tierListUrl(CHORD_SET_RATINGS[set].tier_list_id);
      return <React.Fragment key={idx}>
        {/* eslint-disable-line */}<a className="ratings-set-code" 
            href={setUrl} target="_blank" rel="noopener">
          {set.toUpperCase()}
        </a>
      </React.Fragment>;
    });

  if (setsLinks.length) {
    // wrap in an Expan if more than one source link
    const setsSpan = (setsLinks.length === 1) ?
      <span>{setsLinks}</span>
      : <Expan className="ratings-sources" title="Show sources by set" initial="[&hellip;]">{setsLinks}</Expan>;

    return <>
      <div className="ratings-chord_o_calls">
        <LabelRadio
            name="ratings"
            id="draft-chords_o_calls"
            disabled={disabled}
            value={SOURCE_CHORD_O_CALLS}
            checked={selectedSource === SOURCE_CHORD_O_CALLS}
            onChange={() => setRatingsSourceFn(SOURCE_CHORD_O_CALLS)} >
          <span>
            <em>Draft</em> Ratings from
            <br/>
            {/* eslint-disable-line */} <a href="https://www.youtube.com/@limitedlevel-ups"
              target="_blank" 
              rel="noopener">Limited Level-Ups</a>:
          </span> {setsSpan}
        </LabelRadio>
        {renderMessagesFn(SOURCE_CHORD_O_CALLS)}
      </div>
    </>;
  }
  else return <></>;
}

function maybeRenderLordsOfLimited(sets, selectedSource, disabled, renderMessagesFn, setRatingsSourceFn) {
  const setsLinks = sets.filter(set => LOL_SET_RATINGS.hasOwnProperty(set))
    .map((set, idx) => {
      const setUrl = SeventeenLands.tierListUrl(LOL_SET_RATINGS[set].tier_list_id);
      return <React.Fragment key={idx}>
        {/* eslint-disable-line */}<a className="ratings-set-code" 
            href={setUrl} target="_blank" rel="noopener">
          {set.toUpperCase()}
        </a>
      </React.Fragment>;
    });

  if (setsLinks.length) {
    // wrap in an Expan if more than one source link
    const setsSpan = (setsLinks.length === 1) ?
      <span>{setsLinks}</span>
      : <Expan className="ratings-sources" title="Show sources by set" initial="[&hellip;]">{setsLinks}</Expan>;

    return <>
      <div className="ratings-lordsoflimited">
        <LabelRadio
            name="ratings"
            id="draft-lordsoflimited"
            disabled={disabled}
            value={SOURCE_LORDS_OF_LIMITED}
            checked={selectedSource === SOURCE_LORDS_OF_LIMITED}
            onChange={() => setRatingsSourceFn(SOURCE_LORDS_OF_LIMITED)} >
          <span>
            <em>Draft</em> Ratings from 
            <br/>
            {/* eslint-disable-line */} <a href="https://www.lordsoflimited.com"
              target="_blank" 
              rel="noopener">Lords of Limited</a>:
          </span> {setsSpan}
        </LabelRadio>
        {renderMessagesFn(SOURCE_LORDS_OF_LIMITED)}
      </div>
    </>;
  }
  else return <></>;
}

/**
 * Determine which card rating sources are available for the given sets.
 * 
 * @param {string[]} sets
 * @returns an array of ratings source names, in order of which is preferred (default)
 */
function availableRatingsSources(sets) {
  const sources = [];
  // Darkest Mage
  if (sets?.some(set => DARKEST_MAGE_SET_RATINGS.hasOwnProperty(set))) {
    sources.push(SOURCE_DARKEST_MAGE);
  }
  // Chord_O_Calls
  if (sets?.some(set => CHORD_SET_RATINGS.hasOwnProperty(set))) {
    sources.push(SOURCE_CHORD_O_CALLS);
  }
  // Lords of Limited
  if (sets?.some(set => LOL_SET_RATINGS.hasOwnProperty(set))) {
    sources.push(SOURCE_LORDS_OF_LIMITED);
  }
  // Custom Ratings?
  if (hasCustomRatingsId()) {
    sources.push(SOURCE_CUSTOM);
  }

  return sources;
}

/**
 * Determine which of the given sets are available for the given ratings source.
 * 
 * @param {string[]} sets
 * @param {string} ratingsSource
 * @returns an array of those sets available for the given ratings source.
 */
function availableSetsForSource(sets, ratingsSource) {
  let ratings = {};
  // Darkest Mage
  if (ratingsSource === SOURCE_DARKEST_MAGE) {
    ratings = DARKEST_MAGE_SET_RATINGS;
  }
  // Chord_O_Calls
  else if (ratingsSource === SOURCE_CHORD_O_CALLS) {
    ratings = CHORD_SET_RATINGS;
  }
  // Lords of Limited
  else if (ratingsSource === SOURCE_LORDS_OF_LIMITED) {
    ratings = LOL_SET_RATINGS;
  }

  return sets.filter(set => ratings.hasOwnProperty(set));
}

/**
 * Retrieve the tier list card ratings from 17Lands for each set.
 * 
 * @param {String[]} sets the set codes for which to fetch 17Lands tier list ratings
 * @returns a {@link Promise} containing an array of `ratings` retrieved for each set and an
 * array of `errors` containing the set codes whose card ratings could not be retrieved:
 * ```
   {
     ratings: { 'mh3' : [ { 'name': 'Nulldrifter', ...}, ...], 'set': ...}, 
     errors: [ 'otj', ...]
   }
   ```
 */
async function fetchCardRatings(ratingsSource, sets) {
  // initialize the array of sets to fetch
  const setsToFetch = sets ?? [];

  // empty custom card ratings promise unless SOURCE_CUSTOM
  let customRatingsPromise = Promise.resolve([]);
  const customRatingsSource = customRatingsSourceId();
  if (ratingsSource === SOURCE_CUSTOM && customRatingsSource) {
    customRatingsPromise = SeventeenLands.cardTiers(customRatingsSource[SOURCE_CUSTOM_SEVENTEENLANDS]);

    // await the card tier data and calculate the corresponding card sets
    const customSets = await customRatingsPromise
      .then(SeventeenLands.setsFromCardTiers)
      .catch(error => {
        console.error('Failed to fetch custom card ratings from 17Lands.', error);
        return [];
      });
    if (!customSets?.length) {
      console.warn('Did not find any sanitized card sets among 17Lands card ratings:', customSets);
    }
    // update the array of sets to fetch
    setsToFetch.push.apply(setsToFetch, customSets);
  }

  // wait for all card set stats promises to complete, regardless of whether or not one rejects
  return Promise.allSettled(
    setsToFetch.map(set => {
      // helper function to add the `set` and `source` to the promise result
      const withSetAndSource = (promise) => promise
        .then(json => ({ set, source: ratingsSource, ratings: json}))
        .catch(error => {
          error.set = set;
          error.source = ratingsSource;
          throw error;
        });

      // construct a Promise to retrieve the card tiers/ratings
      if (ratingsSource === SOURCE_CHORD_O_CALLS) {
        return withSetAndSource(SeventeenLands.cardTiersChordOCalls(set));
      }
      else if (ratingsSource === SOURCE_LORDS_OF_LIMITED) {
        return withSetAndSource(SeventeenLands.cardTiersLordsOfLimited(set));
      }
      else if (ratingsSource === SOURCE_DARKEST_MAGE) {
        return withSetAndSource(GoogleSheets.darkestMageRatings(set));
      }
      else if (ratingsSource === SOURCE_CUSTOM) {
        return withSetAndSource(customRatingsPromise);
      }
      // else; create an error for unknown set
      const error = new Error(`Tried to load unknown ratings source '${ratingsSource}' `
          + `for set ${set.toUpperCase()}`);
      return withSetAndSource(Promise.reject(error));
    }))
    .then(extractRatingsAndErrors)
}

/**
 * Extract the 17Lands tier list ratings and any errors from an array of results 
 * from `Promise.allSettled`.
 * 
 * @param {Object[]} allSettledResults 
 * @returns a {@link Promise} containing an array of `ratings` retrieved for each set and an
 * array of `errors` containing the set codes whose card ratings could not be retrieved:
 * ```
   {
     ratings: { 'mh3' : [ { 'name': 'Nulldrifter', ...}, ...], 'set': ...}, 
     errors: [ 'otj', ...]
   }
   ```
 */
function extractRatingsAndErrors(allSettledResults) {
  const cardRatingsBySet = allSettledResults.reduce((combinedResults, result) => {
    if (result.status === 'fulfilled') {
      const set = result.value?.set;
      const source = result.value?.source;
      // filter out any empty tier list ratings and log a warning
      if (result.value?.ratings?.length) {
        if (combinedResults.ratings.hasOwnProperty(set)) {
          console.warn(`Retrieved multiple tier list ratings from ${source} for [${set?.toUpperCase()}]`, 
          combinedResults.ratings[set], result.value.ratings);
          combinedResults.ratings[set] = combinedResults.ratings[set].concat(result.value.ratings);
        }
        else {
          combinedResults.ratings[set] = result.value.ratings;
        }
      }
      else {
        combinedResults.missing = combinedResults.missing.concat(set);
        console.warn(`17Lands tier list ratings not found from ${source} for [${set?.toUpperCase()}]`);
      }
    } else { // result.status === 'rejected'
      // track any sets that had an error while fetching and log an error
      combinedResults.errors = combinedResults.errors.concat(result.reason.set);
      console.error(`Error while loading tier list ratings from ${result.reason.source} ` +
        `for [${result.reason.set?.toUpperCase()}] -`, result.reason);
    }        
    return combinedResults;
  }, /* initialValue = */ { ratings: {}, errors: [], missing: [] });

  return Promise.resolve(cardRatingsBySet);
}

/**
 * Map the card Ids for every provided card to the corresponding card ratings 
 * in the provided `cardRatingsBySet` (matched by lower case card name).
 * 
 * @param {Object[]} cards the cards in the card pool
 * @param {Object} cardRatingsBySet mapping from card set to an array of card tier ratings
 * @returns a {@link Promise} containing an array of each `cardId` and the corresponding 
 *          card rating data (e.g., RATING_TIER_PROPERTY, RATING_SCORES_PROPERTY)
 */
function mapCardIdsToRatings(cards, cardRatingsBySet) {
  if (Object.keys(cardRatingsBySet).length === 0) {
    console.warn(`No tier list card ratings loaded!`);
    return Promise.resolve([]);
  }
  // determine if there are multiple card sets represented in the pool; considering
  // sanitized sets to *not* consider, e.g., special guests 'SPG' as a distinct set
  // (used when determining if the set icon is shown on the Card)
  const sanitizedCardSets = SeventeenLands.sanitizeAllCardSets(cards);
  const hasMultipleSanitizedSets = sanitizedCardSets.length > 1;

  // organize all tier list ratings first by card name, then by set
  const cardRatingsByNameAndSet = Object.entries(cardRatingsBySet).reduce((acc, [set, setRatings]) => {
    setRatings.forEach(cardRating => {
      const name = cardRating.name.toLowerCase();
      acc[name] = acc[name] || {}; // grab the ratings already stored at this `name` or empty
      if (acc[name][set]) {
        console.warn(`Found multiple tier list ratings for ${cardRating.name} in [${set?.toUpperCase()}]`);
      }
      acc[name][set] = cardRating;
    });
    return acc;
  }, /* initialValue = */ {});

  const cardIdsWithRatings = cards.flatMap(card => {
    // try to lookup the card ratings for this card by it's name and set;
    // `SeventeenLands.normalizeCardSet` will resolve bonus sheet sets, etc.
    let ratingsSet = SeventeenLands.normalizeCardSet(card);
    const cardRatingsBySet = lookupByLowerCaseCardName(cardRatingsByNameAndSet, card);

    if (!cardRatingsBySet) {
      console.debug(`No tier list card ratings for card named [${card.name}]`);
      return []; // exit early; filtered out by flatMap
    }

    // identify the ratings for this card's specific set (if available)
    let cardRatings = cardRatingsBySet[ratingsSet];
    // cardRatings could still be undefined!
    if (!cardRatings) {
      console.warn(`No tier list card ratings for card named [${card.name}] ` +
        `from set [${card.set?.toUpperCase()}]`);

      // identify ratings for this card from *any* set already retrieved
      const cardRatingSets = Object.keys(cardRatingsBySet);
      if (cardRatingSets.length) {
        ratingsSet = cardRatingSets[0];
        console.debug(`Using tier list card ratings for card named [${card.name}] ` +
          `from set [${ratingsSet?.toUpperCase()}]` +
          ((cardRatingSets.length > 1)
            ? '; other options: ' + cardRatingSets.map(set => `[${set.toUpperCase()}]`).join(', ')
            : ''));
        cardRatings = cardRatingsBySet[ratingsSet];
      }
      else {
        console.warn(`No tier list card ratings for card named [${card.name}]`);
        return []; // exit early; filtered out by flatMap
      }
    }

    // check if the card set ratings is different from the actual card's set
    const isCardSetMismatch = ratingsSet !== card.set;

    return [{
      cardId: card.cardId,
      [RATING_TIER_PROPERTY]: cardRatings.tier,
      [RATING_SCORES_PROPERTY]: cardRatings.ratings,
      // only include the set if there are multiple sets to discriminate among
      [RATING_SET_PROPERTY]: (hasMultipleSanitizedSets || isCardSetMismatch) ? ratingsSet : undefined
    }];
  });

  return Promise.resolve(cardIdsWithRatings);
}
